import { CheckCircle2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { SignatureBg } from "../components/SignatureBg";

// Business Funding Application — the optional SECOND intake form, sent after the
// initial client intake. Clone of the Alief Financial Services funding
// application: Personal, Business (+ DBA / sole-owner conditionals), up to three
// co-owners, additional business info + documents, and consent. Public page
// (token-authed); the whole payload is stored encrypted server-side.

type Owner = {
  first_name?: string; middle_name?: string; last_name?: string;
  ssn?: string; dob?: string; percentage_owned?: string; email?: string; address?: string;
};
type Form = Record<string, any> & { co_owners?: Owner[] };

const EMPLOYEE_RANGES = ["1-25", "26-50", "51-100", "101-250", "251-500", "500+"];

// Text field. MUST live at module level: defining this inside the page component
// gives it a new identity on every render, so React remounts the <input> on each
// keystroke and focus is lost — the "can only type one letter at a time" bug.
function F({ label, k, type = "text", required = false, placeholder = "", form, set }:
  { label: string; k: string; type?: string; required?: boolean; placeholder?: string; form: Form; set: (k: string, v: any) => void }) {
  return (
    <label className="form-field">
      <span>{label}{required ? " *" : ""}</span>
      <input type={type} value={form[k] ?? ""} placeholder={placeholder} onChange={(e) => set(k, e.target.value)} />
    </label>
  );
}
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

export default function BusinessApplicationPage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<Form>({ co_owners: [{}] });
  const [companyClient, setCompanyClient] = useState<string>("");
  // The company issuing this link — shown in the consent checkbox.
  const [companyName, setCompanyName] = useState<string>("the company");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveTimer = useRef<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`/api/business-applications/${token}`);
        const d = await r.json();
        if (!r.ok || !d.ok) {
          setLoadError(d.error === "expired" ? "This application link has expired." :
            d.error === "already_submitted" ? "This application was already submitted. Thank you!" :
            "We couldn't find this application link.");
          setLoading(false); return;
        }
        const c = d.application.client || {};
        setCompanyClient(c.legal_name || "");
        if (d.company_name) setCompanyName(String(d.company_name));
        // seed from saved draft, else prefill from the client record
        const draft = d.application.draft || {};
        setForm({
          first_name: c.first_name || "", last_name: c.last_name || "", email: c.email || "",
          phone: c.phone || "", address: c.address_line1 || "", city: c.address_city || "",
          state: c.address_state || "", postal_code: c.address_zip || "", country: "US",
          co_owners: [{}],
          ...draft,
        });
      } catch {
        setLoadError("Something went wrong loading your application.");
      }
      setLoading(false);
    })();
  }, [token]);

  // Debounced autosave of the whole payload.
  const scheduleSave = useCallback((next: Form) => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      setSaving(true);
      try { await fetch(`/api/business-applications/${token}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ payload: next }) }); } catch { /* ignore */ }
      setSaving(false);
    }, 1400);
  }, [token]);

  function set(key: string, value: any) {
    setForm((prev) => { const next = { ...prev, [key]: value }; scheduleSave(next); return next; });
  }
  function setOwner(i: number, key: keyof Owner, value: any) {
    setForm((prev) => {
      const co = [...(prev.co_owners || [])]; co[i] = { ...co[i], [key]: value };
      const next = { ...prev, co_owners: co }; scheduleSave(next); return next;
    });
  }
  function addOwner() { setForm((prev) => ({ ...prev, co_owners: [...(prev.co_owners || []), {}].slice(0, 3) })); }
  function removeOwner(i: number) { setForm((prev) => ({ ...prev, co_owners: (prev.co_owners || []).filter((_, idx) => idx !== i) })); }

  async function uploadDoc(kind: "owner_id" | "business_document", file: File) {
    const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
    const r = await fetch(`/api/business-applications/${token}/upload`, { method: "POST", body: fd });
    const d = await r.json();
    if (!r.ok || !d.ok) throw new Error(d.message || "Upload failed");
    return d as { file_url: string; filename: string };
  }
  async function onOwnerId(file: File) {
    try { const d = await uploadDoc("owner_id", file); set("owner_id_url", d.file_url); set("owner_id_name", d.filename); }
    catch (e: any) { setError(e.message); }
  }
  async function onBusinessDoc(file: File) {
    try {
      const d = await uploadDoc("business_document", file);
      setForm((prev) => { const list = [...(prev.business_documents || []), { url: d.file_url, name: d.filename }]; const next = { ...prev, business_documents: list }; scheduleSave(next); return next; });
    } catch (e: any) { setError(e.message); }
  }

  async function submit() {
    setError(null);
    // minimal required checks
    const required: Array<[string, string]> = [
      ["first_name", "First name"], ["last_name", "Last name"], ["email", "Email"], ["dob", "Date of birth"],
      ["ssn", "Social Security Number"], ["address", "Address"], ["city", "City"], ["phone", "Phone"],
      ["state", "State"], ["postal_code", "Postal code"], ["annual_household_income", "Annual household income"],
      ["job_title", "Job title"], ["business_legal_name", "Legal business name"], ["ein", "EIN"],
    ];
    for (const [k, label] of required) { if (!String(form[k] || "").trim()) { setError(`Please fill in: ${label}.`); return; } }
    if (!form.owner_id_url) { setError("Please upload a driver license or state ID."); return; }
    if (!form.agree_terms || !form.consent_comms) { setError("Please agree to the terms and consent to communications to submit."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`/api/business-applications/${token}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ payload: form, submit: true }) });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.message || "Could not submit");
      setDone(true);
    } catch (e: any) { setError(e.message || "Could not submit your application."); }
    setSubmitting(false);
  }

  if (loading) return <div className="intake-page"><div className="intake-card"><p className="muted">Loading…</p></div></div>;
  if (loadError) return (
    <div className="intake-page has-signature-bg"><SignatureBg />
      <div className="intake-card"><p className="muted">{loadError}</p></div>
    </div>
  );
  if (done) return (
    <div className="intake-page"><div className="intake-card thank-you">
      <CheckCircle2 size={40} aria-hidden="true" />
      <h2>Application received.</h2>
      <p className="muted">Thank you{form.first_name ? `, ${form.first_name}` : ""}. Your business funding application is in — our team will review it and follow up.</p>
    </div></div>
  );

  const isSole = form.is_sole_owner !== "no"; // default: sole owner unless they say No

  return (
    <div className="intake-page">
      <div className="intake-card" style={{ maxWidth: 720 }}>
        <div className="intake-header">
          <div>
            <h1 style={{ margin: 0 }}>Business Funding Application</h1>
            <p className="muted small" style={{ margin: "4px 0 0" }}>{companyClient ? `For ${companyClient}. ` : ""}Your information is encrypted and used only to evaluate funding. {saving ? "Saving…" : "Progress saves automatically."}</p>
          </div>
        </div>

        {/* Section 1 — Personal */}
        <h2 className="section-title">Personal information</h2>
        <div className="form-grid">
          <F form={form} set={set} label="First name" k="first_name" required />
          <F form={form} set={set} label="Last name" k="last_name" required />
          <F form={form} set={set} label="Email" k="email" type="email" required />
          <F form={form} set={set} label="Date of birth" k="dob" type="date" required />
          <label className="form-field"><span>Social Security Number *</span>
            <input type="password" value={form.ssn ?? ""} onChange={(e) => set("ssn", e.target.value)} placeholder="9 digits" autoComplete="off" />
            <span className="muted small">Encrypted at rest.</span>
          </label>
          <F form={form} set={set} label="Phone" k="phone" type="tel" required placeholder="+1" />
          <F form={form} set={set} label="Address" k="address" required />
          <F form={form} set={set} label="City" k="city" required />
          <label className="form-field"><span>State *</span>
            <select value={form.state ?? ""} onChange={(e) => set("state", e.target.value)}>
              <option value="">Select…</option>{US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <F form={form} set={set} label="Postal code" k="postal_code" required />
          <F form={form} set={set} label="Country" k="country" placeholder="US" />
          <F form={form} set={set} label="Annual household income" k="annual_household_income" type="number" required />
          <F form={form} set={set} label="Your job title in the company" k="job_title" required />
        </div>

        {/* Section 2 — Business */}
        <h2 className="section-title">Business information</h2>
        <div className="form-grid">
          <F form={form} set={set} label="Legal name of business" k="business_legal_name" required />
          <F form={form} set={set} label="Business name on card" k="business_name_on_card" />
          <F form={form} set={set} label="Business address" k="business_address" />
          <F form={form} set={set} label="City where the business is located" k="business_city" />
          <label className="form-field"><span>State the business is registered in</span>
            <select value={form.business_state ?? ""} onChange={(e) => set("business_state", e.target.value)}>
              <option value="">Select…</option>{US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <F form={form} set={set} label="Business ZIP code" k="business_zip" />
          <F form={form} set={set} label="Business phone number" k="business_phone" type="tel" />
          <F form={form} set={set} label="When did you start doing business?" k="business_start_date" type="date" />
        </div>
        <div className="radio-row">
          <span>Does the business have a DBA / trade name?</span>
          <label><input type="radio" name="has_dba" checked={form.has_dba === "yes"} onChange={() => set("has_dba", "yes")} /> Yes</label>
          <label><input type="radio" name="has_dba" checked={form.has_dba === "no"} onChange={() => set("has_dba", "no")} /> No</label>
        </div>
        {form.has_dba === "yes" && <div className="form-grid"><F form={form} set={set} label="DBA / trade name" k="dba_name" /></div>}
        <div className="radio-row">
          <span>Are your primary business operations in the United States?</span>
          <label><input type="radio" name="ops_us" checked={form.operations_in_us === "yes"} onChange={() => set("operations_in_us", "yes")} /> Yes</label>
          <label><input type="radio" name="ops_us" checked={form.operations_in_us === "no"} onChange={() => set("operations_in_us", "no")} /> No</label>
        </div>
        <div className="radio-row">
          <span>Are you the sole owner of the business?</span>
          <label><input type="radio" name="sole" checked={form.is_sole_owner === "yes"} onChange={() => set("is_sole_owner", "yes")} /> Yes</label>
          <label><input type="radio" name="sole" checked={form.is_sole_owner === "no"} onChange={() => set("is_sole_owner", "no")} /> No</label>
        </div>

        {/* Section 3 — Co-owners (only if not sole owner) */}
        {!isSole && (
          <>
            <h2 className="section-title">Co-owners</h2>
            <p className="muted small">Add each additional owner (up to 3).</p>
            {(form.co_owners || []).map((o, i) => (
              <div key={i} className="co-owner-block">
                <div className="co-owner-head"><strong>Owner {i + 1}</strong>
                  {(form.co_owners || []).length > 1 && <button type="button" className="btn-ghost" onClick={() => removeOwner(i)}>Remove</button>}
                </div>
                <div className="form-grid">
                  <label className="form-field"><span>First name</span><input value={o.first_name ?? ""} onChange={(e) => setOwner(i, "first_name", e.target.value)} /></label>
                  <label className="form-field"><span>Middle name</span><input value={o.middle_name ?? ""} onChange={(e) => setOwner(i, "middle_name", e.target.value)} /></label>
                  <label className="form-field"><span>Last name</span><input value={o.last_name ?? ""} onChange={(e) => setOwner(i, "last_name", e.target.value)} /></label>
                  <label className="form-field"><span>SSN</span><input type="password" autoComplete="off" value={o.ssn ?? ""} onChange={(e) => setOwner(i, "ssn", e.target.value)} /></label>
                  <label className="form-field"><span>Date of birth</span><input type="date" value={o.dob ?? ""} onChange={(e) => setOwner(i, "dob", e.target.value)} /></label>
                  <label className="form-field"><span>Percentage owned</span><input type="number" value={o.percentage_owned ?? ""} onChange={(e) => setOwner(i, "percentage_owned", e.target.value)} /></label>
                  <label className="form-field"><span>Email</span><input type="email" value={o.email ?? ""} onChange={(e) => setOwner(i, "email", e.target.value)} /></label>
                  <label className="form-field"><span>Address</span><input value={o.address ?? ""} onChange={(e) => setOwner(i, "address", e.target.value)} /></label>
                </div>
              </div>
            ))}
            {(form.co_owners || []).length < 3 && <button type="button" className="btn-secondary" onClick={addOwner}>+ Add another owner</button>}
          </>
        )}

        {/* Section 4 — Additional business info */}
        <h2 className="section-title">Additional business information</h2>
        <div className="form-grid">
          <F form={form} set={set} label="Type of business" k="business_type" placeholder="e.g. Consulting" />
          <F form={form} set={set} label="Tax ID number (EIN)" k="ein" required />
          <F form={form} set={set} label="Business category" k="business_category" />
          <label className="form-field"><span>Number of employees</span>
            <select value={form.num_employees ?? ""} onChange={(e) => set("num_employees", e.target.value)}>
              <option value="">Select…</option>{EMPLOYEE_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <F form={form} set={set} label="Annual business revenue / sales" k="annual_revenue" type="number" />
          <F form={form} set={set} label="Years in business" k="years_in_business" type="number" />
        </div>
        <div className="form-grid">
          <label className="form-field"><span>Driver license or State ID (any owner) *</span>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => e.target.files?.[0] && onOwnerId(e.target.files[0])} />
            {form.owner_id_name && <span className="muted small">✓ {form.owner_id_name}</span>}
          </label>
          <label className="form-field"><span>Business documents (EIN letter, articles of organization) — optional</span>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => e.target.files?.[0] && onBusinessDoc(e.target.files[0])} />
            {(form.business_documents || []).map((d: any, i: number) => <span key={i} className="muted small">✓ {d.name}</span>)}
          </label>
        </div>

        {/* Section 5 — Consent */}
        <h2 className="section-title">Consent</h2>
        <label className="check-row"><input type="checkbox" checked={!!form.agree_terms} onChange={(e) => set("agree_terms", e.target.checked)} />
          <span>I agree to the terms &amp; conditions and privacy policy provided by the company.</span></label>
        <label className="check-row"><input type="checkbox" checked={!!form.consent_comms} onChange={(e) => set("consent_comms", e.target.checked)} />
          <span>I consent to receive electronic communications from {companyName}, including updates, promotions, and general credit-improvement tips.</span></label>

        {error && <p className="intake-error" role="alert" style={{ color: "var(--ne-red-dark)", marginTop: 12 }}>{error}</p>}
        <button className="btn-primary" style={{ marginTop: 16, width: "100%" }} onClick={submit} disabled={submitting}>
          {submitting ? "Submitting…" : "Submit application"}
        </button>
      </div>
    </div>
  );
}
